import { APIError } from 'utils/errors/APIError';
import configs from 'configs';
import { SuperFetchReturn } from 'types';
import { isSessionStorageEnabled, retryablePromise } from './generalUtils';
import promiseDebounce from './promiseDebounce';
import { LogWarning } from './logging';
import isBrowser from './isBrowser';

import {
  getStoredQueryParams,
  getPageViewId,
  getUserSessionId,
} from './analyticsHelpers';

const memoryCache: Map<string, { data: unknown }> = new Map();

const inFlightCacheableRequests = new Map<
  string,
  Promise<SuperFetchReturn<unknown>>
>();

export interface SuperFetchOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'HEAD' | 'OPTIONS';
  body?: Record<string, unknown>;
  queryParams?: Record<string, string>;
  headers?: Record<string, string>;
  timeout: number;
  credentials?: 'include' | 'omit' | 'same-origin';
  cache?: {
    type: 'sessionStorage' | 'memory';
    key?: string | null;
    shouldCache?: (response: T) => boolean;
  };
  retry?: {
    maxAttempts: number;
    waitTimeBetweenFails: number;
  };
  debounceOptions?: {
    waitTime: number;
  };
}

async function _fetch<T>(
  url: string,
  {
    method,
    body,
    timeout,
    credentials = 'same-origin',
    queryParams,
    headers,
  }: SuperFetchOptions<T>
): Promise<SuperFetchReturn<T>> {
  const controller = new AbortController();

  if (!timeout) {
    throw new Error(
      `Fetch: Timeout is required for ${url} with method ${method}`
    );
  }

  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Add query parameters to URL if provided
  const finalUrl = queryParams
    ? `${url}${url.includes('?') ? '&' : '?'}${new URLSearchParams(
        queryParams
      )}`
    : url;

  const fetchOptions: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: body && method !== 'GET' ? JSON.stringify(body) : undefined,
    credentials,
    signal: controller.signal,
  };

  try {
    const response = await fetch(finalUrl, fetchOptions);
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      throw new APIError(
        `Request failed with status ${response.status}: ${errorText}`,
        response.status,
        url
      );
    }

    return [null, (await response.json()) as T, { isFromCache: false }];
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof DOMException && error.name === 'AbortError') {
      LogWarning(`${isBrowser() ? 'Browser' : 'Server'} API call Failed`, {
        description: 'Request timed out',
        endpoint: url,
      });
      return [
        new APIError(
          `Request to ${url} timed out after ${timeout}ms`,
          500,
          url
        ),
        null,
        null,
      ];
    }

    LogWarning(`${isBrowser() ? 'Browser' : 'Server'} API call Failed`, {
      description: error instanceof Error ? error.message : 'Unknown error',
      endpoint: url,
    });

    if (error instanceof APIError) {
      return [error, null, null];
    }

    if (error instanceof Error) {
      return [
        new APIError(`API Request Failed: ${error.message}`, 500, url),
        null,
        null,
      ];
    }

    return [new APIError(`Unknown API Error: ${error}`, 500, url), null, null];
  }
}

async function _fetchWithCache<T>(
  url: string,
  options: SuperFetchOptions<T>
): Promise<SuperFetchReturn<T>> {
  let cacheType;
  let cacheKey;

  if (!isBrowser()) {
    LogWarning('superFetch cache is not supported on the server');
    return _fetch<T>(url, options);
  }

  if (options.cache && options.cache.key) {
    cacheKey = options.cache.key;
    cacheType = isSessionStorageEnabled() ? options.cache.type : 'memory';
    let cache;

    if (cacheType === 'sessionStorage') {
      cache = sessionStorage.getItem(cacheKey);
      if (cache) {
        return [null, JSON.parse(cache).data, { isFromCache: true }];
      }
    }

    if (cacheType === 'memory') {
      cache = memoryCache.get(cacheKey);
      if (cache) {
        return [null, cache.data as T, { isFromCache: true }];
      }
    }
  }

  if (cacheKey && inFlightCacheableRequests.has(cacheKey)) {
    return inFlightCacheableRequests.get(cacheKey) as Promise<
      SuperFetchReturn<T>
    >;
  }

  const inFlightRequest = _fetch<T>(url, options);

  if (cacheKey) {
    inFlightCacheableRequests.set(cacheKey, inFlightRequest);
  }

  const [apiError, data] = await inFlightRequest;

  if (cacheKey) {
    inFlightCacheableRequests.delete(cacheKey);
  }

  if (apiError) {
    return [apiError, data, null];
  }

  if (!options.cache) {
    return [null, data, { isFromCache: false }];
  }

  const shouldCache =
    options.cache.shouldCache ||
    ((apiResponse): boolean => Boolean(apiResponse));

  // Store in cache if caching is enabled
  if (options.cache && cacheKey && shouldCache(data)) {
    try {
      if (cacheType === 'sessionStorage') {
        sessionStorage.setItem(cacheKey, JSON.stringify({ data }));
      } else if (cacheType === 'memory') {
        memoryCache.set(cacheKey, { data });
      }
    } catch (error) {
      LogWarning(`Failed to set API results in session storage`, {
        description: error instanceof Error ? error.message : 'Unknown error',
        endpoint: url,
      });
    }
  }

  return [null, data, { isFromCache: false }];
}

function _fetchWithCacheAndRetry<T>(
  url: string,
  options: SuperFetchOptions<T>
): Promise<SuperFetchReturn<T>> {
  if (options.retry) {
    return retryablePromise(
      (isFinalAttempt: boolean) =>
        _fetchWithCache(url, options).then(([error, data, metadata]) => {
          if (error && !isFinalAttempt) {
            throw error;
          }

          return [error, data, metadata];
        }),
      {
        maxRetryAttempts: options.retry.maxAttempts,
        waitTimeBetweenFails: options.retry.waitTimeBetweenFails,
      }
    );
  }

  return _fetchWithCache(url, options);
}

export function triadSuperFetch<T>(
  url: string,
  options: SuperFetchOptions<T>
): Promise<SuperFetchReturn<T>> {
  const updatedOptions = { ...options };

  if (isBrowser()) {
    const triadSessionInfo = {
      sessionId: getUserSessionId(),
      pageViewId: getPageViewId(),
    };

    if (options.method === 'POST') {
      updatedOptions.body = {
        ...updatedOptions.body,
        ...triadSessionInfo,
        queryParams: getStoredQueryParams().requestArray,
        timeout: options.timeout,
      };
    }

    if (options.method === 'GET') {
      updatedOptions.queryParams = {
        ...updatedOptions.queryParams,
        ...triadSessionInfo,
        timeout: options.timeout.toString(),
      };
    }
  }

  if (!isBrowser()) {
    if (!process.env.TRIAD_API_AUTH) {
      throw new APIError('TRIAD_API_AUTH is not set', 500, url);
    }
    updatedOptions.headers = {
      ...updatedOptions.headers,
      Authorization: process.env.TRIAD_API_AUTH,
    };
  }

  return _fetchWithCacheAndRetry(url, updatedOptions);
}

export function wordpressSuperFetch<T>(
  url: string,
  options: SuperFetchOptions<T>
): Promise<SuperFetchReturn<T>> {
  if (isBrowser()) {
    LogWarning('wordpressSuperFetch should only be ran on the server');
  }

  return _fetchWithCacheAndRetry(`${configs.WPHost}${url}`, {
    ...options,
    headers: {
      ...options.headers,
      Host: configs.WPDomain,
      'User-Agent': 'node',
    },
  });
}

export function externalSuperFetch<T>(
  url: string,
  options: SuperFetchOptions<T>
): Promise<SuperFetchReturn<T>> {
  return _fetchWithCacheAndRetry(url, options);
}

export function debouncedSuperFetch<T>(
  triadOrWordpressSuperFetch: typeof _fetchWithCacheAndRetry,
  url: string,
  options: SuperFetchOptions<T>
): () => Promise<SuperFetchReturn<T>> {
  if (!options.debounceOptions) {
    throw new Error('Debounce options are required');
  }

  return promiseDebounce(
    () => triadOrWordpressSuperFetch(url, options),
    options.debounceOptions.waitTime
  );
}
